
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import SelectDropDown from "@/components/SelectDropDown.vue"
import ErrorMessage from "@/components/ErrorMessage.vue"
import {getXsrfHeader, restDownloadFile} from "@/utils/rest"
import {theme} from "@/theme"
import {Plan, Platform} from "@/superannuation/PlanService"
import {format} from "date-fns"
import {QPopupProxy} from "quasar"
import {Inject} from "vue-property-decorator"
import {AsfaVariables, AsfaVariableService} from "@/AsfaVariableService"
import {AxiosError} from "axios"

@Component({
  components: {
    ErrorMessage,
    SelectDropDown,
    PageHeader,
    HelpIcon,
    QPopupProxy,
  },
})
export default class ImportExportView extends Vue {
  @Inject()
  asfaVariableService: AsfaVariableService

  t = theme()
  getXsrfHeader = getXsrfHeader

  $refs: {
    platformSelect: SelectDropDown
    planSelect: SelectDropDown
    tableSelect: SelectDropDown
  }

  platform: Platform | null = null
  plan: Plan | null = null
  table: string | null = null

  asfaVariables: AsfaVariables | null = null
  asfaMessage: string | null = null
  asfaFile: File | null = null

  xhr: XMLHttpRequest | null = null
  rejection: string | null = null

  reportInProgress = false
  asfaInProgress = false

  async created() {
    await this.getAsfa()
  }

  get isReportingDisabled(): boolean {
    return this.table == null
  }

  get uploadUrl(): string | null {
    let url = "/api/csv/import"
    if (this.platform != null) {
      url += `?platformId=${this.platform.id}`
    }

    if (this.plan != null) {
      url += `${this.platform == null ? "?" : "&"}planId=${this.plan.id}`
    }

    return url
  }

  get message(): string | null {
    return this.xhr == null
      ? this.rejection == null
        ? this.asfaMessage
        : this.rejection
      : JSON.parse(this.xhr.response).message
  }

  private platformSelected(platform: Platform | null) {
    this.platform = platform
    if (platform != null) {
      this.$refs.planSelect.model = null
      this.plan = null
    }
  }

  private planSelected(plan: Plan | null) {
    this.plan = plan
  }

  private tableSelected(table: string | null) {
    this.table = table
  }

  private async downloadReport() {
    if (!this.isReportingDisabled) {
      this.reportInProgress = true

      const parameters: Record<string, string> = {}
      if (this.platform != null) {
        parameters.platformId = this.platform.id
      }

      if (this.plan != null) {
        parameters.planId = this.plan.id
      }

      const now = format(new Date(), "ddMMyyyy")

      await restDownloadFile(
        `/api/csv/${this.table!}/export`,
        `${this.table!} - ${now}.csv`,
        parameters
      )
      this.reportInProgress = false
    }
  }

  private async downloadDynamicFields() {
    this.reportInProgress = true
    const now = format(new Date(), "ddMMyyyy")
    await restDownloadFile(`/api/csv/exportDynamicFields`, `DynamicFields - ${now}.csv`)
    this.reportInProgress = false
  }

  private failed(info: {files: Array<File>; xhr: XMLHttpRequest}) {
    this.xhr = info.xhr
  }

  private rejected(rejectedEntries: Array<{failedPropValidation: string; file: File}>) {
    const entry = rejectedEntries.find(Boolean)
    this.rejection = `File: '${entry!.file.name}' type: '${entry!.file.type}' did not pass: '${
      entry!.failedPropValidation
    }' validation constraint`
  }

  private added() {
    this.rejection = null
  }

  private uploaded() {
    this.xhr = null
  }

  get isSaveAsfaDisabled(): boolean {
    const asfa = this.asfaVariables
    return (
      asfa === null ||
      asfa.pensionSingle <= 0 ||
      asfa.pensionCouple <= 0 ||
      asfa.savingsModestSingle <= 0 ||
      asfa.savingsModestCouple <= 0 ||
      asfa.savingsComfortableSingle <= 0 ||
      asfa.savingsComfortableCouple <= 0
    )
  }

  private async getAsfa() {
    this.asfaInProgress = true
    void (await this.asfaVariableService
      .get()
      .then((asfa: AsfaVariables) => {
        this.asfaMessage = null
        this.asfaVariables = asfa
      })
      .catch((reason: AxiosError<any>) => {
        this.asfaMessage = reason.response?.data.message
      })
      .finally(() => (this.asfaInProgress = false)))
  }

  private async loadOrSaveAsfa(save = false) {
    this.asfaInProgress = true
    void (await this.asfaVariableService
      .set(this.asfaVariables, save, this.asfaFile)
      .then((asfa: AsfaVariables) => {
        this.asfaMessage = null
        this.asfaVariables = asfa
        this.asfaFile = null
      })
      .catch((reason: AxiosError<any>) => {
        this.asfaMessage = reason.response?.data.message
      })
      .finally(() => (this.asfaInProgress = false)))
  }

  private async loadAsfa() {
    await this.loadOrSaveAsfa()
  }

  private async saveAsfa() {
    if (!this.isSaveAsfaDisabled) {
      await this.loadOrSaveAsfa(true)
    }
  }
}
